import { FC, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import { useCSVDownloader } from 'react-papaparse';
import { CsvProduct } from '../ProductCsvReader/ProductCsvReader';
import { Product, ProductCategory } from '@models';
import { useGetAllProductCategoriesQuery } from '@services';
import { CUSTOM_FIELD_HEADERS, VALID_HEADERS } from '@constants';
import { getZoneId } from '@helpers';
import { TestCsvData } from '@pages/Products/ProductCsvDownloadModal/CsvTest/TestCSVdata';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
};

const ProductCsvDownloadModal: FC<Props> = ({ isOpen, onClose, products }) => {
  const [includedHeaders, setIncludedHeaders] = useState(VALID_HEADERS);
  const { data: categories } = useGetAllProductCategoriesQuery();

  const { CSVDownloader } = useCSVDownloader();
  const zoneId = getZoneId(categories);

  // Subset of categories - only the subcategories of Menu1.
  const getCategories = useCallback(
    (product: Product) => {
      return product.categories.reduce((productCategories, id) => {
        const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
        return category ? [...productCategories, category] : productCategories;
      }, [] as ProductCategory[]);
    },
    [categories, zoneId],
  );

  // Subset of categories - only the subcategories of Zones.
  const getZones = useCallback(
    (product: Product) => {
      return product.categories.reduce((zones, id) => {
        const zone = categories?.find((z) => z.id === id && z.parent_id === zoneId);
        return zone ? [...zones, zone] : zones;
      }, [] as ProductCategory[]);
    },
    [categories, zoneId],
  );

  const handleChange = (header: string) => {
    TestCsvData.testOnClickCheckBox(header);

    if (includedHeaders.includes(header)) {
      setIncludedHeaders(includedHeaders.filter((h) => h !== header));
    } else {
      setIncludedHeaders([...includedHeaders, header]);
    }
  };

  const getCsvData = () => {
    const csvProducts: CsvProduct[] = [];

    products.forEach((product) => {
      const csvProduct: CsvProduct = {};

      includedHeaders.forEach((header) => {
        if (CUSTOM_FIELD_HEADERS.includes(header)) {
          csvProduct[header] = product.custom_fields?.find((field) => field.name === header)?.value;
        } else {
          csvProduct[header] = (product as any)[header];
        }

        // The custom_url field is an object of { is_customized: boolean; url: string; }.
        // As such, only the URL itself should be exported.
        if (csvProduct[header]?.url) {
          csvProduct[header] = csvProduct[header].url;
        }

        if (Array.isArray(csvProduct[header])) {
          // Map category ID's to category names and export them as a comma-separated string.
          if (header === 'categories' && product.categories.length) {
            csvProduct.categories = getCategories(product).map(({ id }) => categories?.find((category) => category.id === id)?.name);
          }
        }

        if (header === 'zones') {
          csvProduct.zones = getZones(product).map(({ id }) => categories?.find((zone) => zone.id === id)?.name);
        }
      });

      csvProducts.push(csvProduct);
    });

    return csvProducts;
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" data-testid="csv-download-modal">
      <DialogTitle>Download CSV</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pb: 0, mt: 2 }}>
        <FormControl>
          <FormLabel data-testid="fields-include-test" component="legend">
            Fields to include
          </FormLabel>
          <FormGroup data-testid={'check-box-group'} sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            {VALID_HEADERS.map((header) => (
              <FormControlLabel
                control={<Checkbox checked={includedHeaders.includes(header)} onChange={() => handleChange(header)} />}
                key={header}
                label={header}
                sx={{ width: '25%' }}
              />
            ))}
          </FormGroup>
        </FormControl>
        <Box>
          <Button
            onClick={() => {
              TestCsvData.testUnSelectAll([]);
              setIncludedHeaders([]);
            }}
            data-testid="products-download-unselect-all-btn"
          >
            Unselect all
          </Button>
          <Button
            onClick={() => {
              TestCsvData.testSelectAll(VALID_HEADERS);
              setIncludedHeaders(VALID_HEADERS);
            }}
            sx={{ ml: 1 }}
            data-testid="products-download-select-all-btn"
          >
            Select all
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, px: 3 }}>
        <Box>
          <Button type="button" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <CSVDownloader data={() => getCsvData()} filename={`jimbo-export-${new Date().toISOString()}`}>
            <Button data-testid={'dwnld'} onClick={() => TestCsvData.testCsvData(getCsvData())} sx={{ ml: 2 }} variant="contained">
              Download
            </Button>
          </CSVDownloader>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProductCsvDownloadModal;
